import React, { useState } from "react";
import { useUserActions } from "@/hooks/useUserActions";
import { useNow } from "@/hooks/useNow";
import { CURRENT_USER } from "@/api/queries/currentUser";
import { useOrg } from "@/hooks/useOrg";
import { useQuery, useMutation } from "@apollo/client";
import { CircleAlert } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { Bouncer } from "@/components/Bouncer";
import { RiseAction } from "@/components/RiseAction";
import { getDateString } from "@/util/dateUtils";
import {
  sortActions,
  getUserStrategyBoardState,
  StrategyBoardState,
  aggregateActions,
} from "./util";
import { CapacityPicker } from "./CapacityPicker";
import { USER_ACTIONS } from "@/api/queries/userActions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { RiseUserAction } from "@/types/RiseUserAction";
import { RiseActionDetails } from "@/components/RiseActionDetails";
import { useGetActionsReady } from "./useGetActionsReady";
import { useHiddenActionsCount } from "./useHiddenActionsCount";
import { CREATE_ACTION_GOAL } from "@/api/mutations/createActionGoal";
import {
  CreateActionGoalMutation,
  CreateActionGoalMutationVariables,
  RiseUserActionCapacityEnum as Capacity,
} from "@/__generated__/types";
import { USER_HAS_ASSIGNED_STUDENTS } from "@/api/queries/userHasAssignedStudents";
import { UserProgress } from "./SingleTaskProgress";
import { TaskProgress, TaskProgressView } from "./TaskProgress";

export const ActionsTab = () => {
  const { org, loading: orgLoading } = useOrg();
  const { data: userData, loading: userLoading } = useQuery(CURRENT_USER);
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsViewAction, setDetailsViewAction] = useState<
    RiseUserAction | undefined
  >();

  const userId = userData?.me?.id;

  const { ready, loading: loadingActionStatus } = useGetActionsReady();

  const { data: hasStudentsData, loading: loadingHasAssignedStudents } =
    useQuery(USER_HAS_ASSIGNED_STUDENTS, {
      variables: {
        userId: userId || "",
        orgId: org?.id || "",
      },
      skip: !userId && !org?.id,
    });

  const hasAssignedStudents =
    hasStudentsData?.node?.__typename === "User" &&
    hasStudentsData?.node?.hasAssignedStudents;

  const {
    count: hiddenActionsCount,
    loading: loadingHiddenActionsCount,
    error: hiddenActionsCountsError,
  } = useHiddenActionsCount(userId);
  const now = useNow();
  const todaysDate = new Date(now);

  const {
    actions,
    loading: actionsLoading,
    error,
  } = useUserActions(userId, org?.id, todaysDate);

  const [
    createActionGoal,
    { loading: createActionGoalLoading, error: createActionGoalError },
  ] = useMutation<CreateActionGoalMutation, CreateActionGoalMutationVariables>(
    CREATE_ACTION_GOAL,
    {
      refetchQueries: [USER_ACTIONS],
      awaitRefetchQueries: true,
    },
  );

  const handleCapacitySelect = (capacity: Capacity) => {
    // You can also make an API call here to save the selection
    createActionGoal({
      variables: {
        orgId: org?.id || "",
        userId: userId || "",
        date: getDateString(todaysDate),
        capacity,
      },
    });
  };

  if (
    orgLoading ||
    userLoading ||
    actionsLoading ||
    createActionGoalLoading ||
    loadingHiddenActionsCount ||
    loadingActionStatus ||
    loadingHasAssignedStudents
  ) {
    return <Bouncer />;
  }

  if (error || hiddenActionsCountsError || createActionGoalError) {
    return (
      <div className="flex h-[180px] flex-col items-center justify-center">
        <CircleAlert className="h-12 w-12 text-muted-foreground" />
        <div className="mt-5 text-center text-sm text-muted-foreground">
          <Trans
            i18nKey="global.generalError.message"
            components={{
              br: <br />,
              supportLink: (
                <a
                  href="mailto:support@zeemee.com"
                  className="text-blue-primary hover:underline"
                />
              ),
            }}
          />
        </div>
      </div>
    );
  }

  const boardState = getUserStrategyBoardState(
    actions?.length || 0,
    hiddenActionsCount || 0,
    hasAssignedStudents,
    ready,
  );

  if (boardState !== StrategyBoardState.SHOW_ACTIONS) {
    return (
      <div className="h-full w-full items-center justify-center">
        <CapacityPicker
          firstName={userData?.me.firstName || ""}
          boardState={boardState}
          onCapacitySelect={handleCapacitySelect}
        />
      </div>
    );
  }

  const { done, skipped, total } = aggregateActions(actions || []);

  let userProgress: UserProgress | null = null;
  const user = userData?.me;
  if (user) {
    userProgress = {
      id: user.id,
      name: user.name,
      profileUrl: user.profilePhoto?.url || "",
      completed: done,
      skipped,
      total,
    };
  }

  return (
    <>
      <div className="mx-6 my-8 pb-17">
        <h2 className="text-xl font-bold">
          {t("strategyBoard.actions.actionsForDate", {
            date: todaysDate.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }),
          })}
        </h2>
        <div className="mb-4 text-muted-foreground">
          {t("strategyBoard.actions.description")}
        </div>
        <div className="flex flex-col gap-y-4">
          {userProgress && (
            <TaskProgress
              users={[userProgress]}
              view={TaskProgressView.Single}
            />
          )}
          {sortActions(actions || []).map((action) => (
            <RiseAction
              key={action.id}
              action={action}
              onClick={(action) => {
                setModalOpen(true);
                setDetailsViewAction(action);
              }}
            />
          ))}
        </div>
      </div>
      <ActionDetailsDialog
        action={detailsViewAction}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export function ActionDetailsDialog({
  action,
  open,
  onClose,
}: {
  action?: RiseUserAction;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-full gap-0 overflow-y-auto focus-visible:outline-0 sm:max-w-[1219px]">
        <DialogHeader className="-mx-6 mb-5 border-b px-6 pb-5">
          <DialogTitle>{action?.actionType.displayName}</DialogTitle>
        </DialogHeader>
        {action && <RiseActionDetails action={action} />}
      </DialogContent>
    </Dialog>
  );
}
